import React from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { DialogClose } from '@/components/ui/dialog';
import { CheckCircle2, ExternalLink } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { orgPath } from '@/utils/org-path';

interface SaveRunAsWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  newWorkflowId: string | null;
  newWorkflowName: string | null;
}

export const SaveRunAsWorkflowModal: React.FC<SaveRunAsWorkflowModalProps> = ({
  isOpen,
  onClose,
  newWorkflowId,
  newWorkflowName,
}) => {
  const handleOpenNewWorkflow = () => {
    if (newWorkflowId) {
      window.open(
        orgPath(`${ROUTES.WORKFLOW.ROOT}/${newWorkflowId}`),
        '_blank',
      );
      onClose();
    }
  };

  const Footer = () => (
    <>
      <DialogClose asChild>
        <Button onClick={onClose} variant='outline'>
          Stay Here
        </Button>
      </DialogClose>
      <Button onClick={handleOpenNewWorkflow} disabled={!newWorkflowId}>
        <ExternalLink className='size-4 mr-2' />
        Open New Workflow
      </Button>
    </>
  );

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={(open) => {
        if (!open) {
          onClose();
        }
      }}
      title={
        <div className='flex items-center gap-2'>
          <CheckCircle2 className='size-5 text-green-500' />
          Workflow Created Successfully
        </div>
      }
      footer={<Footer />}
    >
      <div className='space-y-4'>
        <p className='text-sm text-muted-foreground'>
          A new workflow has been created from the selected run.
        </p>

        {newWorkflowName && (
          <div className='rounded-md bg-green-50 dark:bg-green-950/20 p-3 border border-green-200 dark:border-green-900'>
            <p className='text-sm text-green-800 dark:text-green-200'>
              <strong>New workflow:</strong>{' '}
              <span className='font-medium'>{newWorkflowName}</span>
            </p>
          </div>
        )}

        <p className='text-sm text-muted-foreground'>
          You can open the new workflow now or stay on this page.
        </p>
      </div>
    </Modal>
  );
};
